import axiosInstance from '../../services/api_helper';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormGroup,
    Label,
    Input,
    Table,
    Badge,
    Row,
    Col
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { formatPrice } from '../../helpers/utility';
import { invoiceActions } from '../../sagas/invoiceSlice';

const ClientPaymentModal = ({ isOpen, toggle, onSuccess }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedClient, setSelectedClient] = useState(null);
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('ESPECES');
    const [amount, setAmount] = useState('');
    const [reference, setReference] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }));

    const fetchUnpaidInvoices = async (clientId) => {
        try {
            const response = await axiosInstance.get(`/api/invoices/unpaid/${clientId}`);
            const invoices = response.data?.content || [];
            setUnpaidInvoices(invoices.map(invoice => ({
                ...invoice,
                restePaye: invoice.montantTotalTTC - (invoice.montantPaye || 0)
            })));
        } catch (error) {
            console.error('Error fetching unpaid invoices:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('invoice.fetchError')
            });
        }
    };

    useEffect(() => {
        if (selectedClient) {
            fetchUnpaidInvoices(selectedClient.value);
            // Reset selections when client changes
            setSelectedInvoices([]);
            setAmount('');
            setReference('');
        } else {
            setUnpaidInvoices([]);
        }
    }, [selectedClient]);

    const calculateTotal = () => {
        return selectedInvoices.reduce((total, invoiceId) => {
            const invoice = unpaidInvoices.find(inv => inv.id === invoiceId);
            return total + (invoice?.restePaye || 0);
        }, 0);
    };

    const handleInvoiceSelect = (invoiceId) => {
        setSelectedInvoices(prev => {
            if (prev.includes(invoiceId)) {
                return prev.filter(id => id !== invoiceId);
            }
            return [...prev, invoiceId];
        });
    };

    const loadClientOptions = async (inputValue) => {
        try {
            const response = await axiosInstance.get('/api/clients/search', {
                params: { query: inputValue || '', page: 0, size: 10 }
            });
            return (response.data?.content || []).map(client => ({
                value: client.id,
                label: client.nom + (client.prenom ? ' ' + client.prenom : '')
            }));
        } catch (error) {
            console.error('Error loading clients:', error);
            return [];
        }
    };

    const handlePaymentSubmit = () => {
        if (!selectedClient) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('client.required')
            });
            return;
        }

        if (selectedInvoices.length === 0) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('payment.invoiceRequired')
            });
            return;
        }

        if (!amount || parseFloat(amount) <= 0) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('payment.amountRequired')
            });
            return;
        }

        const total = calculateTotal();
        if (parseFloat(amount) > total) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('payment.amountExceedsTotal', { total: formatPrice(total) })
            });
            return;
        }

        if (paymentMethod !== 'ESPECES' && !reference) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('payment.referenceRequired')
            });
            return;
        }

        const payment = {
            clientId: selectedClient.value,
            invoiceIds: selectedInvoices,
            amount: parseFloat(amount),
            paymentMethod,
            paymentDate,
            reference: reference || null,
        };

        dispatch(invoiceActions.addPayment({
            data: payment,
            onSuccess: () => {
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('payment.success'),
                    showConfirmButton: false,
                    timer: 1500
                });
                toggle();
                if (onSuccess) onSuccess();
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.message || t('payment.error')
                });
            }
        }));
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>
                {t('payment.newPayment')}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>{t('client.select')}</Label>
                            <Select
                                isClearable
                                placeholder={t('client.searchPlaceholder')}
                                loadOptions={loadClientOptions}
                                onChange={setSelectedClient}
                                value={selectedClient}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>{t('payment.date')}</Label>
                            <Input
                                type="date"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                {selectedClient && (
                    <div className="mt-4">
                        <h5>{t('payment.unpaidInvoices')}</h5>
                        <Table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>{t('invoice.number')}</th>
                                    <th>{t('invoice.date')}</th>
                                    <th>{t('invoice.amount')}</th>
                                    <th>{t('invoice.paid')}</th>
                                    <th>{t('invoice.remaining')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unpaidInvoices.map(invoice => (
                                    <tr key={invoice.id}>
                                        <td>
                                            <Input
                                                type="checkbox"
                                                checked={selectedInvoices.includes(invoice.id)}
                                                onChange={() => handleInvoiceSelect(invoice.id)}
                                            />
                                        </td>
                                        <td>{invoice.numero}</td>
                                        <td>{new Date(invoice.date).toLocaleDateString()}</td>
                                        <td>{formatPrice(invoice.montantTotalTTC)}</td>
                                        <td>{formatPrice(invoice.montantPaye)}</td>
                                        <td>{formatPrice(invoice.restePaye)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                <Row className="mt-4">
                    <Col md={4}>
                        <FormGroup>
                            <Label>{t('payment.method')}</Label>
                            <Input
                                type="select"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                                <option value="ESPECES">{t('payment.cash')}</option>
                                <option value="CHEQUE">{t('payment.check')}</option>
                                <option value="VIREMENT">{t('payment.transfer')}</option>
                                <option value="CARD">{t('payment.card')}</option>
                                <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>{t('payment.amount')}</Label>
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                min={0}
                                step="0.01"
                            />
                        </FormGroup>
                    </Col>
                    {paymentMethod !== 'ESPECES' && (
                        <Col md={4}>
                            <FormGroup>
                                <Label>{t('payment.reference')}</Label>
                                <Input
                                    type="text"
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    placeholder={t('payment.referencePlaceholder')}
                                />
                            </FormGroup>
                        </Col>
                    )}
                </Row>

                <div className="mt-3 text-end">
                    <h5>
                        {t('payment.total')}: <Badge color="success">{formatPrice(calculateTotal())}</Badge>
                    </h5>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    {t('common.cancel')}
                </Button>
                <Button color="primary" onClick={handlePaymentSubmit}>
                    {t('payment.submit')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ClientPaymentModal;
